import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const vintagePress: TemplateRegistration = {
  meta: {
    id: 'vintage-press',
    name: 'Vintage Press',
    category: 'Classic',
    description: 'Old newspaper style with multi-column text layout',
    previewImage: '',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Merriweather', 'PT Sans']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="about-bleed" ${heroImage ? `style="background-image:url('${escapeAttr(heroImage)}')"` : `style="background:var(--accent)"`}>
        <div class="about-bleed-overlay"></div>
        <div class="about-bleed-content">
          <h2 class="section-title">About Us</h2>
          <p class="about-text">${safeAbout}</p>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceItems = data.services.map((s, i) => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        const num = String(i + 1).padStart(2, '0');
        return `
          <div class="service-numbered-item">
            <span class="service-num">${num}</span>
            <div class="service-body">
              <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
              <h3>${sName}</h3>
              <p>${sDesc}</p>
              ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Our Services</h2>
          <div class="services-numbered">${serviceItems}</div>
          <div class="press-rule"></div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="section section-parchment">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <div class="testimonial-inline">
              <div class="testimonial-body"><p>&ldquo;${tContent}&rdquo;</p></div>
              <div class="testimonial-author">
                <strong>&mdash; ${tName}</strong>
                ${tRole ? `<span>, ${tRole}</span>` : ''}
              </div>
            </div>
            ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Testimonials</h2>
          <div class="testimonials-list">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section section-parchment">
        <div class="container">
          <h2 class="section-title">Frequently Asked Questions</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section section-parchment">
        <div class="container">
          <h2 class="section-title">Hours</h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Get In Touch</h2>
          <div class="contact-cards">
            <div class="contact-card">
              <div class="contact-card-title">Contact Info</div>
              <div class="contact-info">
                ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
                ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
                ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Maps</a>` : ''}
              </div>
            </div>
            <div class="contact-card contact-form-card">
              <div class="contact-card-title">Send a Message</div>
              <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
                <input type="text" placeholder="Your Name" required>
                <input type="email" placeholder="Your Email" required>
                <textarea placeholder="Message" rows="4" required></textarea>
                <button type="submit">Send Message</button>
              </form>
            </div>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #704214; --bg: #F5E6CC; --bg-alt: #EBD9B8; --accent: #704214;
      --text: #2C1810; --text-muted: #6B5340;
      --font-heading: 'Merriweather', serif; --font-body: 'PT Sans', sans-serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.7; }
    .container { max-width: 1000px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--accent); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* PRESS RULES */
    .press-rule { width: 100%; height: 1px; background: var(--accent); margin: 32px 0 0; }
    .press-rule-double { width: 100%; height: 0; border-top: 3px double var(--accent); margin: 0 0 24px; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(245,230,204,.95); backdrop-filter: blur(8px); border-bottom: 3px double var(--accent); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 14px 24px; max-width: 1000px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.4rem; color: var(--text); font-weight: 900; text-transform: uppercase; letter-spacing: 3px; }
    .nav-links { display: flex; gap: 24px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .8rem; text-transform: uppercase; letter-spacing: 2px; font-weight: 700; }
    .nav-links a:hover { color: var(--accent); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 24px; height: 2px; background: var(--accent); margin: 5px 0; transition: .3s; }

    /* HERO - newspaper-style multi-column */
    .hero { position: relative; min-height: 100vh; overflow: hidden; display: flex; align-items: center; }
    .hero-bg { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: .1; }
    .hero-overlay { position: absolute; inset: 0; background: linear-gradient(to bottom, rgba(245,230,204,.5), var(--bg)); }
    .hero-content { position: relative; z-index: 2; padding: 120px 24px 60px; max-width: 1000px; margin: 0 auto; width: 100%; }
    .hero-masthead { border-top: 3px double var(--accent); border-bottom: 3px double var(--accent); padding: 16px 0; margin-bottom: 24px; text-align: center; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2.5rem, 7vw, 5rem); line-height: 1.1; font-weight: 900; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 8px; color: var(--text); }
    .hero-columns { column-count: 3; column-gap: 32px; column-rule: 1px solid rgba(112,66,20,.25); }
    .hero-columns p { font-size: .95rem; color: var(--text-muted); line-height: 1.7; margin-bottom: 16px; }
    .hero-cta-wrap { text-align: center; margin-top: 32px; }
    .hero-cta { display: inline-block; padding: 14px 36px; background: var(--accent); color: var(--bg); font-family: var(--font-heading); font-size: .95rem; text-transform: uppercase; letter-spacing: 2px; font-weight: 700; border: none; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(112,66,20,.3); opacity: 1; color: var(--bg); }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section-parchment { background: var(--bg); }
    .section-alt { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.6rem, 4vw, 2.4rem); text-align: center; text-transform: uppercase; letter-spacing: 3px; margin-bottom: 48px; color: var(--text); position: relative; }
    .section-title::before, .section-title::after { content: ''; display: block; width: 60px; height: 1px; background: var(--accent); margin: 0 auto; }
    .section-title::before { margin-bottom: 16px; }
    .section-title::after { margin-top: 16px; }

    /* ABOUT - full-bleed with overlay */
    .about-bleed { position: relative; min-height: 400px; background-size: cover; background-position: center; display: flex; align-items: center; }
    .about-bleed-overlay { position: absolute; inset: 0; background: rgba(44,24,16,.8); }
    .about-bleed-content { position: relative; z-index: 2; max-width: 700px; margin: 0 auto; text-align: center; padding: 60px 24px; }
    .about-bleed-content .section-title { color: var(--bg); }
    .about-text { font-size: 1.05rem; color: rgba(245,230,204,.85); line-height: 1.8; }

    /* SERVICES - numbered list */
    .services-numbered { display: flex; flex-direction: column; gap: 0; max-width: 800px; margin: 0 auto; }
    .service-numbered-item { display: flex; align-items: flex-start; gap: 24px; padding: 28px 0; border-bottom: 1px solid rgba(112,66,20,.2); }
    .service-numbered-item:last-child { border-bottom: none; }
    .service-num { font-family: var(--font-heading); font-size: 3rem; font-weight: 900; color: rgba(112,66,20,.2); line-height: 1; min-width: 56px; text-align: center; }
    .service-body { flex: 1; }
    .service-icon { color: var(--accent); margin-bottom: 8px; }
    .icon-char { font-size: 24px; line-height: 1; }
    .service-body h3 { font-family: var(--font-heading); font-size: 1.1rem; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px; }
    .service-body p { color: var(--text-muted); font-size: .9rem; line-height: 1.6; }
    .service-price { display: inline-block; margin-top: 10px; color: var(--accent); font-weight: 700; font-size: 1rem; }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
    .gallery-item img { width: 100%; height: 220px; object-fit: cover; display: block; filter: sepia(.15); }
    .gallery-item img:hover { filter: sepia(0); }

    /* TESTIMONIALS - minimal inline */
    .testimonials-list { display: flex; flex-direction: column; gap: 20px; max-width: 800px; margin: 0 auto; }
    .testimonial-card { padding: 16px 0; border-bottom: 1px solid rgba(112,66,20,.15); }
    .testimonial-card:last-child { border-bottom: none; }
    .testimonial-body p { font-family: var(--font-heading); font-size: 1.05rem; font-style: italic; color: var(--text-muted); line-height: 1.7; display: inline; }
    .testimonial-inline { display: block; }
    .testimonial-author { display: inline; color: var(--text); font-size: .95rem; }
    .testimonial-author strong { color: var(--text); font-size: .95rem; }
    .testimonial-author span { color: var(--text-muted); font-size: .85rem; font-style: italic; }
    .testimonial-stars { margin-top: 6px; }
    .star { color: #C4B09E; font-size: 1rem; }
    .star.filled { color: var(--accent); }

    /* FAQ */
    .faq-list { max-width: 800px; margin: 0 auto; }
    .faq-item { border-bottom: 1px solid rgba(112,66,20,.2); padding: 24px 0; }
    .faq-q { font-family: var(--font-heading); font-size: 1.1rem; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 1px; }
    .faq-a { color: var(--text-muted); line-height: 1.7; }

    /* PRICE LIST */
    .price-list { max-width: 800px; margin: 0 auto; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 24px 0; border-bottom: 1px solid rgba(112,66,20,.2); gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.05rem; margin-bottom: 4px; }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.4rem; color: var(--accent); white-space: nowrap; font-weight: 700; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; margin: 0 auto; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid rgba(112,66,20,.2); color: var(--text-muted); font-size: .95rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--accent); padding: 16px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: var(--bg); font-size: 1.2rem; }
    .review-text { color: var(--bg); font-weight: 700; font-size: .95rem; }

    /* PROMO */
    .promo-banner { background: rgba(255,255,255,.3); padding: 20px 0; border-top: 3px double var(--accent); border-bottom: 3px double var(--accent); }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; justify-content: center; }
    .promo-badge { background: var(--accent); color: var(--bg); font-family: var(--font-heading); padding: 8px 20px; font-size: 1rem; text-transform: uppercase; letter-spacing: 1px; }
    .promo-content strong { display: block; margin-bottom: 2px; }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT - three-col cards */
    .contact-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; align-items: start; }
    .contact-card { background: rgba(255,255,255,.5); padding: 32px 24px; text-align: center; border-top: 3px double var(--accent); }
    .contact-card-title { font-family: var(--font-heading); font-size: 1rem; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 12px; color: var(--text); }
    .contact-info { display: flex; flex-direction: column; gap: 12px; align-items: center; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--accent); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 600; text-transform: uppercase; font-size: .85rem; letter-spacing: 1px; }
    .contact-form-card { grid-column: span 2; }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea { background: rgba(255,255,255,.5); border: 1px solid rgba(112,66,20,.3); color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--accent); }
    .contact-form button { padding: 16px; background: var(--accent); color: var(--bg); font-family: var(--font-heading); font-size: .95rem; text-transform: uppercase; letter-spacing: 2px; font-weight: 700; border: none; cursor: pointer; transition: transform .2s; }
    .contact-form button:hover { transform: translateY(-2px); }

    /* FOOTER */
    .footer { background: var(--accent); padding: 40px 0; text-align: center; }
    .footer p { color: rgba(245,230,204,.7); font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; }
      .nav-links.show { display: flex; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(245,230,204,.98); padding: 16px 24px; gap: 12px; border-bottom: 3px double var(--accent); }
      .nav-toggle { display: block; }
      .hero-columns { column-count: 1; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .contact-cards { grid-template-columns: 1fr; }
      .contact-form-card { grid-column: span 1; }
      .price-row { flex-direction: column; gap: 8px; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 100px 16px 60px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    ${heroImage ? `<div class="hero-bg" style="background-image:url('${escapeAttr(heroImage)}')"></div>` : ''}
    <div class="hero-overlay"></div>
    <div class="hero-content">
      <div class="hero-masthead">
        <h1>${safeHeadline}</h1>
      </div>
      <div class="hero-columns">
        <p>${truncateAtSentence(safeAbout, 400)}</p>
      </div>
      <div class="hero-cta-wrap">
        ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Contact Us</a>` : ''}
      </div>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
